import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import styles from './LevantamentoAppCartaoCredito.module.scss';

const STORAGE_KEY = 'levantamento-app-cartao-credito-v1';

type CustomField = {
    id: string;
    name: string;
    description: string;
};

type FormState = {
    projectName: string;
    objective: string;
    audience: string;
    cardFields: Record<string, boolean>;
    customCardFields: CustomField[];
    compareCards: string;
    compareFields: Record<string, boolean>;
    customCompareFields: CustomField[];
    filterCards: string;
    filters: Record<string, boolean>;
    customFilters: CustomField[];
    vipFields: Record<string, boolean>;
    customVipFields: CustomField[];
    hotelFields: Record<string, boolean>;
    customHotelFields: CustomField[];
    dataSource: string;
    dataSourceOther: string;
    dataExists: string;
    dataLocation: string;
    dataLocationOther: string;
    firstLoadResponsible: string;
    dataOrganized: string;
    qtyCards: string;
    qtyVip: string;
    qtyHotels: string;
    qtyOther: string;
    updateFrequency: string;
    updateFrequencyOther: string;
    adminActions: Record<string, boolean>;
    customAdminActions: CustomField[];
    otherFeatures: string;
    referenceUrl: string;
    referenceLikes: string;
    extraNotes: string;
};

const CARD_FIELDS = [
    'Nome do cartão',
    'Banco/emissor',
    'Bandeira',
    'Categoria do cartão',
    'Anuidade',
    'Renda mínima',
    'Pontuação',
    'Programa de pontos',
    'Milhas',
    'Cashback',
    'Benefícios',
    'Benefícios para viagens',
    'Acesso a salas VIP',
    'Quantidade de acessos às salas VIP',
    'Regras de acesso às salas VIP',
    'Benefícios em hotéis',
    'Benefícios em restaurantes',
    'Seguros',
    'Outros benefícios',
    'Regras e condições',
    'Link para solicitação',
    'Site oficial',
    'Imagem/logo',
    'Data da última atualização',
];

const FILTER_FIELDS = [
    'Banco/emissor',
    'Bandeira',
    'Categoria',
    'Anuidade',
    'Renda mínima',
    'Pontuação',
    'Milhas',
    'Cashback',
    'Salas VIP',
    'Benefícios',
    'Benefícios para viagens',
    'Outros',
];

const VIP_FIELDS = [
    'Nome da sala',
    'Aeroporto',
    'Código do aeroporto',
    'Terminal',
    'Localização',
    'Cidade',
    'País',
    'Horário de funcionamento',
    'Regras de acesso',
    'Quantidade de acompanhantes',
    'Tempo máximo de permanência',
    'Serviços disponíveis',
    'Cartões que permitem acesso',
    'Programas que permitem acesso',
    'Fotos',
    'Observações',
    'Data da última atualização',
];

const HOTEL_FIELDS = [
    'Nome do hotel',
    'Rede hoteleira',
    'Cidade',
    'País',
    'Endereço',
    'Categoria',
    'Programa de fidelidade',
    'Benefícios',
    'Regras para obtenção dos benefícios',
    'Fotos',
    'Link para reserva',
    'Site oficial',
    'Contato',
    'Observações',
    'Data da última atualização',
];

const ADMIN_ACTIONS = [
    'Cadastrar cartões',
    'Editar cartões',
    'Excluir cartões',
    'Cadastrar salas VIP',
    'Editar salas VIP',
    'Excluir salas VIP',
    'Cadastrar hotéis',
    'Editar hotéis',
    'Excluir hotéis',
    'Alterar benefícios',
    'Atualizar informações',
    'Enviar notificações',
    'Gerenciar usuários',
    'Visualizar relatórios',
    'Outro',
];

const SECTIONS = [
    { id: 'geral', title: 'Informações gerais' },
    { id: 'cartoes', title: 'Cartões de crédito' },
    { id: 'comparacao', title: 'Comparação' },
    { id: 'filtros', title: 'Filtros' },
    { id: 'salas', title: 'Salas VIP' },
    { id: 'hoteis', title: 'Hotéis' },
    { id: 'origem', title: 'Origem dos dados' },
    { id: 'carga', title: 'Primeira carga' },
    { id: 'admin', title: 'Administração' },
    { id: 'outras', title: 'Outras funcionalidades' },
    { id: 'obs', title: 'Observações' },
    { id: 'resumo', title: 'Resumo' },
];

function mapFrom(list: string[], checked: boolean): Record<string, boolean> {
    return list.reduce((acc, item) => {
        acc[item] = checked;
        return acc;
    }, {} as Record<string, boolean>);
}

function createEmptyForm(): FormState {
    return {
        projectName: '',
        objective: '',
        audience: '',
        cardFields: mapFrom(CARD_FIELDS, true),
        customCardFields: [],
        compareCards: '',
        compareFields: {},
        customCompareFields: [],
        filterCards: '',
        filters: mapFrom(FILTER_FIELDS, false),
        customFilters: [],
        vipFields: mapFrom(VIP_FIELDS, true),
        customVipFields: [],
        hotelFields: mapFrom(HOTEL_FIELDS, true),
        customHotelFields: [],
        dataSource: '',
        dataSourceOther: '',
        dataExists: '',
        dataLocation: '',
        dataLocationOther: '',
        firstLoadResponsible: '',
        dataOrganized: '',
        qtyCards: '',
        qtyVip: '',
        qtyHotels: '',
        qtyOther: '',
        updateFrequency: '',
        updateFrequencyOther: '',
        adminActions: mapFrom(ADMIN_ACTIONS, false),
        customAdminActions: [],
        otherFeatures: '',
        referenceUrl: '',
        referenceLikes: '',
        extraNotes: '',
    };
}

function loadForm(): FormState {
    const empty = createEmptyForm();
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return empty;
        const saved = JSON.parse(raw) as Partial<FormState>;
        return {
            ...empty,
            ...saved,
            cardFields: { ...empty.cardFields, ...saved.cardFields },
            compareFields: { ...empty.compareFields, ...saved.compareFields },
            filters: { ...empty.filters, ...saved.filters },
            vipFields: { ...empty.vipFields, ...saved.vipFields },
            hotelFields: { ...empty.hotelFields, ...saved.hotelFields },
            adminActions: { ...empty.adminActions, ...saved.adminActions },
            customCardFields: saved.customCardFields ?? [],
            customCompareFields: saved.customCompareFields ?? [],
            customFilters: saved.customFilters ?? [],
            customVipFields: saved.customVipFields ?? [],
            customHotelFields: saved.customHotelFields ?? [],
            customAdminActions: saved.customAdminActions ?? [],
        };
    } catch {
        return empty;
    }
}

function selectedLabels(map: Record<string, boolean>, custom: CustomField[] = []): string[] {
    const base = Object.entries(map)
        .filter(([, on]) => on)
        .map(([label]) => label);
    const extra = custom.filter((item) => item.name.trim()).map((item) => {
        return item.description.trim() ? `${item.name} (${item.description})` : item.name;
    });
    return [...base, ...extra];
}

function textOrDash(value: string): string {
    const trimmed = value.trim();
    return trimmed ? trimmed : 'Não informado';
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function buildPdfHtml(form: FormState): string {
    const date = new Date().toLocaleString('pt-BR');
    const project = textOrDash(form.projectName);
    const qa = (question: string, answer: string) =>
        `<div class="row">
            <div class="q">${escapeHtml(question)}</div>
            <div class="a">${escapeHtml(answer || 'Não informado')}</div>
        </div>`;
    const chips = (title: string, items: string[]) =>
        `<div class="block">
            <p class="block-title">${escapeHtml(title)}</p>
            ${
                items.length
                    ? `<div class="chips">${items
                          .map((item) => `<span class="chip">${escapeHtml(item)}</span>`)
                          .join('')}</div>`
                    : `<p class="empty">Nenhum item selecionado</p>`
            }
        </div>`;
    const custom = (title: string, items: CustomField[]) =>
        items.length
            ? `<div class="block">
                <p class="block-title">${escapeHtml(title)}</p>
                <table class="custom-table">
                    <thead><tr><th>Campo</th><th>Descrição</th></tr></thead>
                    <tbody>${items
                        .map(
                            (item) =>
                                `<tr><td>${escapeHtml(item.name)}</td><td>${escapeHtml(item.description || '—')}</td></tr>`
                        )
                        .join('')}</tbody>
                </table>
            </div>`
            : '';
    const section = (number: string, title: string, body: string) =>
        `<section class="section">
            <h2><span class="num">${number}</span>${escapeHtml(title)}</h2>
            ${body}
        </section>`;

    const dataSource =
        form.dataSource === 'Outro' && form.dataSourceOther.trim()
            ? `${form.dataSource}: ${form.dataSourceOther}`
            : form.dataSource;
    const dataLocation =
        form.dataLocation === 'Outro' && form.dataLocationOther.trim()
            ? `${form.dataLocation}: ${form.dataLocationOther}`
            : form.dataLocation;
    const update =
        form.updateFrequency === 'Outro' || form.updateFrequencyOther.trim()
            ? [form.updateFrequency, form.updateFrequencyOther].filter(Boolean).join(': ')
            : form.updateFrequency;

    return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8"/>
<title>Levantamento - ${escapeHtml(project)}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    color: #111827;
    font-family: "Segoe UI", Calibri, Arial, sans-serif;
    font-size: 11.5px;
    line-height: 1.45;
    background: #fff;
  }
  .sheet {
    padding: 22mm 20mm 24mm;
  }
  .cover {
    border-bottom: 3px solid #1d4ed8;
    padding: 0 0 16px;
    margin-bottom: 18px;
  }
  .kicker {
    margin: 0 0 6px;
    color: #1d4ed8;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }
  h1 {
    margin: 0 0 6px;
    font-size: 22px;
    line-height: 1.2;
    color: #0f172a;
  }
  .lead {
    margin: 0 0 12px;
    color: #475569;
    font-size: 12.5px;
  }
  .meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 12px;
  }
  .meta b { display: block; color: #64748b; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; }
  .meta span { color: #0f172a; font-weight: 600; }
  h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid #dbeafe;
    font-size: 13.5px;
    color: #0f172a;
    page-break-after: avoid;
  }
  .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #1d4ed8;
    color: #fff;
    font-size: 10px;
  }
  .section { margin: 0 0 16px; page-break-inside: avoid; }
  .row {
    display: grid;
    grid-template-columns: 210px 1fr;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #f1f5f9;
    page-break-inside: avoid;
  }
  .q { color: #64748b; font-weight: 650; }
  .a { color: #0f172a; white-space: pre-wrap; word-break: break-word; }
  .block { margin: 8px 0 4px; page-break-inside: avoid; }
  .block-title { margin: 0 0 8px; color: #64748b; font-weight: 650; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip {
    display: inline-block;
    padding: 4px 8px;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    border-radius: 999px;
    color: #1e3a8a;
    font-size: 10.5px;
  }
  .empty { margin: 0; color: #94a3b8; font-style: italic; }
  table.qty, table.custom-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 4px;
  }
  table.qty th, table.qty td, table.custom-table th, table.custom-table td {
    border: 1px solid #e2e8f0;
    padding: 7px 8px;
    text-align: left;
  }
  table.qty th, table.custom-table th {
    background: #f8fafc;
    color: #475569;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  table.qty td { font-weight: 700; }
  .footer {
    margin-top: 18px;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;
    color: #94a3b8;
    font-size: 10px;
  }
  @media print {
    @page { size: A4; margin: 0; }
    html, body { margin: 0 !important; padding: 0 !important; }
    .sheet { padding: 22mm 20mm 24mm !important; }
    .section { break-inside: avoid; }
    .row, .block { break-inside: avoid; }
  }
</style>
</head>
<body>
<div class="sheet">
  <header class="cover">
    <p class="kicker">Documento interno</p>
    <h1>Levantamento Inicial de Requisitos</h1>
    <p class="lead">Aplicativo de cartões de crédito, salas VIP e hotéis</p>
    <div class="meta">
      <div><b>Projeto</b><span>${escapeHtml(project)}</span></div>
      <div><b>Data de preenchimento</b><span>${escapeHtml(date)}</span></div>
    </div>
  </header>

  ${section('1', 'Informações gerais do aplicativo', [
      qa('Nome do projeto', textOrDash(form.projectName)),
      qa('Objetivo do aplicativo', textOrDash(form.objective)),
      qa('Quem utilizará o aplicativo?', textOrDash(form.audience)),
  ].join(''))}

  ${section('2', 'Cartões de crédito do Brasil', [
      chips('Informações selecionadas para cada cartão', selectedLabels(form.cardFields)),
      custom('Campos personalizados', form.customCardFields),
  ].join(''))}

  ${section('3', 'Comparação de cartões', [
      qa('O usuário poderá comparar cartões?', textOrDash(form.compareCards)),
      form.compareCards === 'Sim'
          ? chips('Informações utilizadas na comparação', selectedLabels(form.compareFields, form.customCompareFields))
          : '',
      form.compareCards === 'Sim' ? custom('Campos personalizados da comparação', form.customCompareFields) : '',
  ].join(''))}

  ${section('4', 'Filtros para cartões', [
      qa('O usuário poderá filtrar os cartões?', textOrDash(form.filterCards)),
      form.filterCards === 'Sim'
          ? chips('Filtros selecionados', selectedLabels(form.filters, form.customFilters))
          : '',
      form.filterCards === 'Sim' ? custom('Filtros personalizados', form.customFilters) : '',
  ].join(''))}

  ${section('5', 'Salas VIP', [
      chips('Informações selecionadas para cada sala VIP', selectedLabels(form.vipFields)),
      custom('Campos personalizados', form.customVipFields),
  ].join(''))}

  ${section('6', 'Hotéis', [
      chips('Informações selecionadas para cada hotel', selectedLabels(form.hotelFields)),
      custom('Campos personalizados', form.customHotelFields),
  ].join(''))}

  ${section('7', 'Origem e manutenção dos dados', [
      qa('De onde virão os dados dos cartões, salas VIP e hotéis?', textOrDash(dataSource)),
      qa('Atualmente esses dados já existem?', textOrDash(form.dataExists)),
      form.dataExists === 'Sim' || form.dataExists === 'Parcialmente'
          ? qa('Onde esses dados estão atualmente?', textOrDash(dataLocation))
          : '',
  ].join(''))}

  ${section('8', 'Primeira carga de dados', [
      qa('Quem será responsável pela primeira carga de dados?', textOrDash(form.firstLoadResponsible)),
      qa('Os dados serão entregues já organizados e padronizados?', textOrDash(form.dataOrganized)),
      `<div class="block">
        <p class="block-title">Quantidade aproximada de registros</p>
        <table class="qty">
          <thead><tr><th>Cartões</th><th>Salas VIP</th><th>Hotéis</th><th>Outros</th></tr></thead>
          <tbody><tr>
            <td>${escapeHtml(textOrDash(form.qtyCards))}</td>
            <td>${escapeHtml(textOrDash(form.qtyVip))}</td>
            <td>${escapeHtml(textOrDash(form.qtyHotels))}</td>
            <td>${escapeHtml(textOrDash(form.qtyOther))}</td>
          </tr></tbody>
        </table>
      </div>`,
      qa('Previsão de atualização desses dados', textOrDash(update)),
  ].join(''))}

  ${section('9', 'Administração do aplicativo', [
      chips('O que o painel deverá permitir', selectedLabels(form.adminActions, form.customAdminActions)),
      custom('Ações personalizadas', form.customAdminActions),
  ].join(''))}

  ${section('10', 'Outras funcionalidades desejadas', [
      qa('Outras funcionalidades', textOrDash(form.otherFeatures)),
      qa('Aplicativo ou site de referência', textOrDash(form.referenceUrl)),
      qa('O que vocês gostam nessa referência?', textOrDash(form.referenceLikes)),
  ].join(''))}

  ${section('11', 'Observações adicionais', [
      qa('Informações ainda não mencionadas e importantes para o projeto', textOrDash(form.extraNotes)),
  ].join(''))}

  <p class="footer">Documento gerado automaticamente a partir do levantamento inicial de requisitos.</p>
</div>
</body>
</html>`;
}

function RadioGroup({
    name,
    options,
    value,
    onChange,
}: {
    name: string;
    options: string[];
    value: string;
    onChange: (next: string) => void;
}) {
    return (
        <div className={styles.options}>
            {options.map((option) => (
                <label
                    key={option}
                    className={`${styles.option} ${value === option ? styles.optionActive : ''}`}
                >
                    <input
                        type="radio"
                        name={name}
                        checked={value === option}
                        onChange={() => onChange(option)}
                    />
                    <span>{option}</span>
                </label>
            ))}
        </div>
    );
}

function Checklist({
    items,
    values,
    onToggle,
}: {
    items: string[];
    values: Record<string, boolean>;
    onToggle: (item: string) => void;
}) {
    return (
        <div className={styles.checklist}>
            {items.map((item) => (
                <label
                    key={item}
                    className={`${styles.option} ${values[item] ? styles.optionActive : ''}`}
                >
                    <input type="checkbox" checked={!!values[item]} onChange={() => onToggle(item)} />
                    <span>{item}</span>
                </label>
            ))}
        </div>
    );
}

function CustomFields({
    items,
    onAdd,
    onRemove,
}: {
    items: CustomField[];
    onAdd: (field: CustomField) => void;
    onRemove: (id: string) => void;
}) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const add = () => {
        if (!name.trim()) return;
        onAdd({
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            name: name.trim(),
            description: description.trim(),
        });
        setName('');
        setDescription('');
    };

    return (
        <div className={styles.addBox}>
            <p className={styles.addTitle}>+ Adicionar outro campo</p>
            {items.length > 0 && (
                <div className={styles.options} style={{ marginBottom: '0.7rem' }}>
                    {items.map((item) => (
                        <label key={item.id} className={`${styles.option} ${styles.optionActive}`}>
                            <input type="checkbox" checked readOnly />
                            <span>
                                {item.name}
                                {item.description ? ` — ${item.description}` : ''}
                            </span>
                            <button
                                type="button"
                                className={styles.ghostBtn}
                                style={{ marginLeft: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                onClick={() => onRemove(item.id)}
                            >
                                Remover
                            </button>
                        </label>
                    ))}
                </div>
            )}
            <div className={styles.field}>
                <span className={styles.label}>Nome do campo</span>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className={styles.field}>
                <span className={styles.label}>Descrição (opcional)</span>
                <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className={styles.addActions}>
                <button type="button" className={styles.ghostBtn} onClick={add}>
                    Adicionar
                </button>
            </div>
        </div>
    );
}

function isIosDevice(): boolean {
    const ua = window.navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const iPadOs = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
    return iOS || iPadOs;
}

function fileNameFromProject(projectName: string): string {
    const slug =
        (projectName || 'levantamento')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase() || 'levantamento';
    return `${slug}-levantamento.pdf`;
}

async function savePdfFile(pdf: InstanceType<typeof jsPDF>, filename: string): Promise<'shared' | 'downloaded' | 'opened'> {
    const blob = pdf.output('blob');
    const file = new File([blob], filename, { type: 'application/pdf' });
    const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
        share?: (data: ShareData) => Promise<void>;
    };

    if (typeof nav.canShare === 'function' && typeof nav.share === 'function') {
        try {
            if (nav.canShare({ files: [file] })) {
                await nav.share({ files: [file], title: filename });
                return 'shared';
            }
        } catch (error) {
            if ((error as Error).name === 'AbortError') return 'shared';
        }
    }

    const url = URL.createObjectURL(blob);
    if (isIosDevice()) {
        const opened = window.open(url, '_blank');
        if (!opened) {
            window.location.assign(url);
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        return 'opened';
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    return 'downloaded';
}

const LevantamentoAppCartaoCredito: React.FC = () => {
    const [form, setForm] = useState<FormState>(loadForm);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [iosPdfLink, setIosPdfLink] = useState<string | null>(null);
    const ready = useRef(false);

    useEffect(() => {
        ready.current = true;
    }, []);

    useEffect(() => {
        if (!ready.current) return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    }, [form]);

    const patch = (partial: Partial<FormState>) => setForm((prev) => ({ ...prev, ...partial }));

    const toggleMap = (key: keyof FormState, item: string) => {
        setForm((prev) => {
            const map = { ...(prev[key] as Record<string, boolean>) };
            map[item] = !map[item];
            return { ...prev, [key]: map };
        });
    };

    const addCustom = (key: keyof FormState, field: CustomField) => {
        setForm((prev) => ({ ...prev, [key]: [...(prev[key] as CustomField[]), field] }));
    };

    const removeCustom = (key: keyof FormState, id: string) => {
        setForm((prev) => ({
            ...prev,
            [key]: (prev[key] as CustomField[]).filter((item) => item.id !== id),
        }));
    };

    const compareOptions = useMemo(() => {
        const selected = Object.entries(form.cardFields)
            .filter(([, on]) => on)
            .map(([label]) => label);
        const custom = form.customCardFields.map((item) => item.name).filter(Boolean);
        return [...selected, ...custom];
    }, [form.cardFields, form.customCardFields]);

    const completedSections = useMemo(() => {
        const flags = [
            !!(form.projectName || form.objective || form.audience),
            selectedLabels(form.cardFields, form.customCardFields).length > 0,
            !!form.compareCards,
            !!form.filterCards,
            selectedLabels(form.vipFields, form.customVipFields).length > 0,
            selectedLabels(form.hotelFields, form.customHotelFields).length > 0,
            !!(form.dataSource || form.dataExists),
            !!(form.firstLoadResponsible || form.dataOrganized || form.updateFrequency),
            selectedLabels(form.adminActions, form.customAdminActions).length > 0,
            !!(form.otherFeatures || form.referenceUrl || form.referenceLikes),
            !!form.extraNotes,
            !!(form.projectName && form.objective),
        ];
        return flags.filter(Boolean).length;
    }, [form]);

    const progress = Math.round((completedSections / SECTIONS.length) * 100);

    const goTo = (index: number) => {
        const next = Math.max(0, Math.min(SECTIONS.length - 1, index));
        setSectionIndex(next);
        document.getElementById(SECTIONS[next].id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const generatePdf = async () => {
        if (generatingPdf) return;
        setGeneratingPdf(true);
        if (iosPdfLink) {
            URL.revokeObjectURL(iosPdfLink);
            setIosPdfLink(null);
        }

        const host = document.createElement('iframe');
        host.setAttribute('aria-hidden', 'true');
        Object.assign(host.style, {
            position: 'fixed',
            left: '0',
            top: '0',
            width: '794px',
            height: '1200px',
            border: '0',
            opacity: '0.01',
            zIndex: '-1',
            pointerEvents: 'none',
        });
        document.body.appendChild(host);

        try {
            await new Promise<void>((resolve) => {
                let settled = false;
                const finish = () => {
                    if (settled) return;
                    settled = true;
                    resolve();
                };
                host.onload = finish;
                host.srcdoc = buildPdfHtml(form);
                window.setTimeout(finish, 900);
            });

            const iframeDoc = host.contentDocument;
            if (!iframeDoc) throw new Error('Não foi possível gerar o PDF.');
            const sheet = (iframeDoc.querySelector('.sheet') as HTMLElement | null) ?? iframeDoc.body;
            host.style.height = `${Math.max(sheet.scrollHeight, sheet.offsetHeight, 1200) + 48}px`;
            await new Promise((resolve) => window.setTimeout(resolve, 120));

            const canvas = await html2canvas(sheet, {
                scale: isIosDevice() ? 1.25 : 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                windowWidth: 794,
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.92);
            const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
            while (heightLeft > 1) {
                position -= pageHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const filename = fileNameFromProject(form.projectName);
            await savePdfFile(pdf, filename);
            if (isIosDevice()) {
                setIosPdfLink(URL.createObjectURL(pdf.output('blob')));
            }
        } catch {
            window.alert('Não foi possível gerar o PDF neste aparelho. Tente novamente ou use um computador.');
        } finally {
            host.remove();
            setGeneratingPdf(false);
        }
    };

    const clearForm = () => {
        if (!window.confirm('Deseja limpar todo o formulário? Essa ação não pode ser desfeita.')) return;
        const empty = createEmptyForm();
        setForm(empty);
        localStorage.removeItem(STORAGE_KEY);
        setSectionIndex(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className={styles['levantamento-app-cartao-credito']}>
            <div className={styles.wrap}>
                <header className={styles.header}>
                    <div className={styles.headerTop}>
                        <div>
                            <p className={styles.eyebrow}>Levantamento inicial</p>
                            <h1 className={styles.title}>Requisitos do aplicativo</h1>
                        </div>
                        <button type="button" className={styles.clearBtn} onClick={clearForm}>
                            Limpar formulário
                        </button>
                    </div>
                    <div className={styles.progressRow}>
                        <span className={styles.progressLabel}>Progresso: {progress}%</span>
                        <div className={styles.progressTrack}>
                            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                    <div className={styles.nav}>
                        <button
                            type="button"
                            className={styles.navBtn}
                            disabled={sectionIndex === 0}
                            onClick={() => goTo(sectionIndex - 1)}
                        >
                            Voltar
                        </button>
                        <button
                            type="button"
                            className={styles.navBtn}
                            disabled={sectionIndex === SECTIONS.length - 1}
                            onClick={() => goTo(sectionIndex + 1)}
                        >
                            Próximo
                        </button>
                    </div>
                </header>

                <section id="geral" className={styles.section}>
                    <h2 className={styles.sectionTitle}>1. Informações gerais do aplicativo</h2>
                    <p className={styles.help}>
                        Conte, em linguagem simples, o que o aplicativo deve fazer e para quem ele existe.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>Nome do projeto</span>
                        <input
                            type="text"
                            value={form.projectName}
                            onChange={(e) => patch({ projectName: e.target.value })}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>Objetivo do aplicativo</span>
                        <span className={styles.hint}>
                            Qual problema ele resolve? O que a pessoa consegue fazer ao usar o app?
                        </span>
                        <textarea
                            className={styles.textareaLg}
                            value={form.objective}
                            onChange={(e) => patch({ objective: e.target.value })}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>Público-alvo</span>
                        <span className={styles.hint}>Quem utilizará o aplicativo?</span>
                        <textarea
                            value={form.audience}
                            onChange={(e) => patch({ audience: e.target.value })}
                        />
                    </div>
                </section>

                <section id="cartoes" className={styles.section}>
                    <h2 className={styles.sectionTitle}>2. Cartões de crédito do Brasil</h2>
                    <p className={styles.help}>
                        O aplicativo deverá apresentar informações sobre cartões de crédito disponíveis no
                        Brasil, incluindo seus benefícios, características e regras.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>Quais informações deverão ser apresentadas para cada cartão?</span>
                        <span className={styles.hint}>Marque o que deve aparecer na ficha de cada cartão.</span>
                        <Checklist
                            items={CARD_FIELDS}
                            values={form.cardFields}
                            onToggle={(item) => toggleMap('cardFields', item)}
                        />
                        <CustomFields
                            items={form.customCardFields}
                            onAdd={(field) => addCustom('customCardFields', field)}
                            onRemove={(id) => removeCustom('customCardFields', id)}
                        />
                    </div>
                </section>

                <section id="comparacao" className={styles.section}>
                    <h2 className={styles.sectionTitle}>3. Comparação de cartões</h2>
                    <div className={styles.field}>
                        <span className={styles.label}>O usuário poderá comparar cartões?</span>
                        <RadioGroup
                            name="compareCards"
                            options={['Sim', 'Não', 'Ainda não definido']}
                            value={form.compareCards}
                            onChange={(compareCards) => patch({ compareCards })}
                        />
                    </div>
                    {form.compareCards === 'Sim' && (
                        <div className={styles.field}>
                            <span className={styles.label}>Quais informações deverão ser utilizadas na comparação?</span>
                            <Checklist
                                items={compareOptions}
                                values={form.compareFields}
                                onToggle={(item) => toggleMap('compareFields', item)}
                            />
                            <CustomFields
                                items={form.customCompareFields}
                                onAdd={(field) => addCustom('customCompareFields', field)}
                                onRemove={(id) => removeCustom('customCompareFields', id)}
                            />
                        </div>
                    )}
                </section>

                <section id="filtros" className={styles.section}>
                    <h2 className={styles.sectionTitle}>4. Filtros para cartões</h2>
                    <div className={styles.field}>
                        <span className={styles.label}>O usuário poderá filtrar os cartões?</span>
                        <RadioGroup
                            name="filterCards"
                            options={['Sim', 'Não', 'Ainda não definido']}
                            value={form.filterCards}
                            onChange={(filterCards) => patch({ filterCards })}
                        />
                    </div>
                    {form.filterCards === 'Sim' && (
                        <div className={styles.field}>
                            <span className={styles.label}>Quais filtros deverão existir?</span>
                            <Checklist
                                items={FILTER_FIELDS}
                                values={form.filters}
                                onToggle={(item) => toggleMap('filters', item)}
                            />
                            <CustomFields
                                items={form.customFilters}
                                onAdd={(field) => addCustom('customFilters', field)}
                                onRemove={(id) => removeCustom('customFilters', id)}
                            />
                        </div>
                    )}
                </section>

                <section id="salas" className={styles.section}>
                    <h2 className={styles.sectionTitle}>5. Salas VIP</h2>
                    <p className={styles.help}>
                        O aplicativo poderá apresentar informações sobre salas VIP de aeroportos e seus
                        critérios de acesso.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>Quais informações deverão ser cadastradas para cada sala VIP?</span>
                        <Checklist
                            items={VIP_FIELDS}
                            values={form.vipFields}
                            onToggle={(item) => toggleMap('vipFields', item)}
                        />
                        <CustomFields
                            items={form.customVipFields}
                            onAdd={(field) => addCustom('customVipFields', field)}
                            onRemove={(id) => removeCustom('customVipFields', id)}
                        />
                    </div>
                </section>

                <section id="hoteis" className={styles.section}>
                    <h2 className={styles.sectionTitle}>6. Hotéis</h2>
                    <p className={styles.help}>
                        O aplicativo poderá apresentar informações sobre hotéis, redes hoteleiras, programas
                        de fidelidade e benefícios.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>Quais informações deverão ser cadastradas para cada hotel?</span>
                        <Checklist
                            items={HOTEL_FIELDS}
                            values={form.hotelFields}
                            onToggle={(item) => toggleMap('hotelFields', item)}
                        />
                        <CustomFields
                            items={form.customHotelFields}
                            onAdd={(field) => addCustom('customHotelFields', field)}
                            onRemove={(id) => removeCustom('customHotelFields', id)}
                        />
                    </div>
                </section>

                <section id="origem" className={styles.section}>
                    <h2 className={styles.sectionTitle}>7. Origem e manutenção dos dados</h2>
                    <p className={styles.help}>
                        Como as informações chegarão ao aplicativo? Não é preciso saber termos técnicos: escolha
                        a opção que mais se parece com a realidade de vocês.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>De onde virão os dados dos cartões, salas VIP e hotéis?</span>
                        <RadioGroup
                            name="dataSource"
                            options={[
                                'Cadastro manual pelo cliente',
                                'Cadastro manual pela nossa equipe',
                                'Importação de Excel',
                                'Importação de CSV',
                                'Banco de dados existente',
                                'API',
                                'Outro',
                                'Ainda não definido',
                            ]}
                            value={form.dataSource}
                            onChange={(dataSource) => patch({ dataSource })}
                        />
                    </div>
                    {form.dataSource === 'Outro' && (
                        <div className={styles.field}>
                            <span className={styles.label}>Explique como os dados serão disponibilizados</span>
                            <input
                                type="text"
                                value={form.dataSourceOther}
                                onChange={(e) => patch({ dataSourceOther: e.target.value })}
                            />
                        </div>
                    )}
                    <div className={styles.field}>
                        <span className={styles.label}>Atualmente esses dados já existem?</span>
                        <RadioGroup
                            name="dataExists"
                            options={['Sim', 'Não', 'Parcialmente', 'Ainda não definido']}
                            value={form.dataExists}
                            onChange={(dataExists) => patch({ dataExists })}
                        />
                    </div>
                    {(form.dataExists === 'Sim' || form.dataExists === 'Parcialmente') && (
                        <div className={styles.field}>
                            <span className={styles.label}>Onde esses dados estão atualmente?</span>
                            <RadioGroup
                                name="dataLocation"
                                options={['Excel', 'CSV', 'Banco de dados', 'Sistema próprio', 'Site', 'API', 'Outro']}
                                value={form.dataLocation}
                                onChange={(dataLocation) => patch({ dataLocation })}
                            />
                        </div>
                    )}
                    {form.dataLocation === 'Outro' && (
                        <div className={styles.field}>
                            <span className={styles.label}>Informe onde os dados estão hoje</span>
                            <input
                                type="text"
                                value={form.dataLocationOther}
                                onChange={(e) => patch({ dataLocationOther: e.target.value })}
                            />
                        </div>
                    )}
                </section>

                <section id="carga" className={styles.section}>
                    <h2 className={styles.sectionTitle}>8. Primeira carga de dados</h2>
                    <p className={styles.help}>
                        Precisamos entender quem será responsável por preparar e cadastrar os dados iniciais
                        que estarão disponíveis no aplicativo.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>Quem será responsável pela primeira carga de dados?</span>
                        <RadioGroup
                            name="firstLoadResponsible"
                            options={[
                                'Cliente',
                                'Nossa equipe',
                                'Cliente fornecerá os dados e nossa equipe realizará a importação',
                                'Ainda não definido',
                            ]}
                            value={form.firstLoadResponsible}
                            onChange={(firstLoadResponsible) => patch({ firstLoadResponsible })}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>Os dados serão entregues já organizados e padronizados?</span>
                        <RadioGroup
                            name="dataOrganized"
                            options={['Sim', 'Não', 'Parcialmente', 'Ainda não definido']}
                            value={form.dataOrganized}
                            onChange={(dataOrganized) => patch({ dataOrganized })}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>Quantidade aproximada de registros</span>
                        <div className={styles.qtyGrid}>
                            <label className={styles.field}>
                                <span className={styles.label}>Cartões</span>
                                <input
                                    type="number"
                                    min={0}
                                    value={form.qtyCards}
                                    onChange={(e) => patch({ qtyCards: e.target.value })}
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.label}>Salas VIP</span>
                                <input
                                    type="number"
                                    min={0}
                                    value={form.qtyVip}
                                    onChange={(e) => patch({ qtyVip: e.target.value })}
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.label}>Hotéis</span>
                                <input
                                    type="number"
                                    min={0}
                                    value={form.qtyHotels}
                                    onChange={(e) => patch({ qtyHotels: e.target.value })}
                                />
                            </label>
                            <label className={styles.field}>
                                <span className={styles.label}>Outros registros</span>
                                <input
                                    type="number"
                                    min={0}
                                    value={form.qtyOther}
                                    onChange={(e) => patch({ qtyOther: e.target.value })}
                                />
                            </label>
                        </div>
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>Existe uma previsão de atualização desses dados?</span>
                        <RadioGroup
                            name="updateFrequency"
                            options={[
                                'Manual',
                                'Diária',
                                'Semanal',
                                'Mensal',
                                'Eventual',
                                'Automática via integração/API',
                                'Ainda não definido',
                                'Outro',
                            ]}
                            value={form.updateFrequency}
                            onChange={(updateFrequency) => patch({ updateFrequency })}
                        />
                    </div>
                    {(form.updateFrequency === 'Outro' || form.updateFrequency === 'Automática via integração/API') && (
                        <div className={styles.field}>
                            <span className={styles.label}>Detalhes da atualização (opcional)</span>
                            <input
                                type="text"
                                value={form.updateFrequencyOther}
                                onChange={(e) => patch({ updateFrequencyOther: e.target.value })}
                            />
                        </div>
                    )}
                </section>

                <section id="admin" className={styles.section}>
                    <h2 className={styles.sectionTitle}>9. Administração do aplicativo</h2>
                    <p className={styles.help}>
                        O aplicativo terá um painel administrativo para cadastrar e atualizar o conteúdo,
                        sem precisar alterar o código. Marque o que essa área precisa permitir.
                    </p>
                    <div className={styles.field}>
                        <span className={styles.label}>O que o painel deverá permitir?</span>
                        <Checklist
                            items={ADMIN_ACTIONS}
                            values={form.adminActions}
                            onToggle={(item) => toggleMap('adminActions', item)}
                        />
                        <CustomFields
                            items={form.customAdminActions}
                            onAdd={(field) => addCustom('customAdminActions', field)}
                            onRemove={(id) => removeCustom('customAdminActions', id)}
                        />
                    </div>
                </section>

                <section id="outras" className={styles.section}>
                    <h2 className={styles.sectionTitle}>10. Outras funcionalidades desejadas</h2>
                    <div className={styles.field}>
                        <span className={styles.label}>
                            Descreva outras funcionalidades que não foram contempladas neste formulário
                        </span>
                        <textarea
                            className={styles.textareaLg}
                            value={form.otherFeatures}
                            onChange={(e) => patch({ otherFeatures: e.target.value })}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>
                            Existe algum aplicativo ou site que vocês utilizam como referência?
                        </span>
                        <input
                            type="url"
                            placeholder="https://"
                            value={form.referenceUrl}
                            onChange={(e) => patch({ referenceUrl: e.target.value })}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>O que vocês gostam nessa referência?</span>
                        <textarea
                            value={form.referenceLikes}
                            onChange={(e) => patch({ referenceLikes: e.target.value })}
                        />
                    </div>
                </section>

                <section id="obs" className={styles.section}>
                    <h2 className={styles.sectionTitle}>11. Observações adicionais</h2>
                    <div className={styles.field}>
                        <span className={styles.label}>
                            Existe alguma informação, regra, necessidade ou funcionalidade que ainda não foi
                            mencionada e que vocês consideram importante para o projeto?
                        </span>
                        <textarea
                            className={styles.textareaLg}
                            value={form.extraNotes}
                            onChange={(e) => patch({ extraNotes: e.target.value })}
                        />
                    </div>
                </section>

                <section id="resumo" className={styles.section}>
                    <h2 className={styles.sectionTitle}>Resumo do levantamento</h2>
                    <p className={styles.help}>Revise as respostas principais antes de gerar o documento.</p>
                    <div className={styles.summary}>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Nome do projeto</p>
                            <p className={styles.summaryValue}>{textOrDash(form.projectName)}</p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Objetivo</p>
                            <p className={styles.summaryValue}>{textOrDash(form.objective)}</p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Público-alvo</p>
                            <p className={styles.summaryValue}>{textOrDash(form.audience)}</p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Funcionalidades selecionadas</p>
                            <p className={styles.summaryValue}>
                                Comparar cartões: {textOrDash(form.compareCards)}
                                {'\n'}Filtrar cartões: {textOrDash(form.filterCards)}
                                {'\n'}Campos de cartão: {selectedLabels(form.cardFields, form.customCardFields).length}
                                {'\n'}Campos de salas VIP: {selectedLabels(form.vipFields, form.customVipFields).length}
                                {'\n'}Campos de hotéis: {selectedLabels(form.hotelFields, form.customHotelFields).length}
                            </p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Origem dos dados</p>
                            <p className={styles.summaryValue}>{textOrDash(form.dataSource)}</p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Responsável pela primeira carga</p>
                            <p className={styles.summaryValue}>{textOrDash(form.firstLoadResponsible)}</p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Painel administrativo</p>
                            <p className={styles.summaryValue}>
                                {selectedLabels(form.adminActions, form.customAdminActions).join(', ') || 'Não informado'}
                            </p>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Outras informações importantes</p>
                            <p className={styles.summaryValue}>
                                {textOrDash(form.otherFeatures || form.extraNotes)}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.primaryBtn}
                        onClick={generatePdf}
                        disabled={generatingPdf}
                    >
                        {generatingPdf ? 'Gerando PDF...' : 'Gerar PDF do levantamento'}
                    </button>
                    {iosPdfLink ? (
                        <a
                            className={styles.primaryBtn}
                            href={iosPdfLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Abrir PDF no iPhone
                        </a>
                    ) : null}
                    <p className={styles.pdfNote}>
                        No iPhone, o Safari pode abrir o compartilhamento. Escolha <strong>Salvar em Arquivos</strong>.
                        Se isso não aparecer, use o botão <strong>Abrir PDF no iPhone</strong>.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default LevantamentoAppCartaoCredito;
